import React, { useCallback, useState, useEffect } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown as ChevronDownIcon,
} from "lucide-react";
import {
  getValidYears,
  getMinimumValidBirthDate,
  validateBirthDate,
} from "../../utils/ageRestriction";

interface DatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDateSelect: (date: Date) => void;
  selectedDate: Date | null;
  title?: string;
  disablePastDates?: boolean;
  maxDate?: Date;
  minDate?: Date;
}

export const DatePickerModal: React.FC<DatePickerModalProps> = ({
  isOpen,
  onClose,
  onDateSelect,
  selectedDate,
  title = "Select Date",
  disablePastDates = false,
  maxDate,
  minDate,
}) => {
  const getValidDefaultDate = useCallback((): Date => {
    const effectiveMaxDate = maxDate
      ? new Date(maxDate)
      : getMinimumValidBirthDate();
    effectiveMaxDate.setHours(0, 0, 0, 0);

    const effectiveMinDate = minDate
      ? new Date(minDate)
      : new Date("1900-01-01");
    effectiveMinDate.setHours(0, 0, 0, 0);

    if (selectedDate) {
      const selected = new Date(selectedDate);
      selected.setHours(0, 0, 0, 0);
      if (selected >= effectiveMinDate && selected <= effectiveMaxDate) {
        return selected;
      }
    }

    return new Date(effectiveMaxDate);
  }, [maxDate, minDate, selectedDate]);

  const [currentMonth, setCurrentMonth] = useState<Date>(getValidDefaultDate());
  const [error, setError] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      const validDate = getValidDefaultDate();
      setCurrentMonth(validDate);
      setHasInteracted(!!selectedDate);
    }
  }, [isOpen, selectedDate, getValidDefaultDate]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleDateClick = useCallback(
    (date: Date) => {
      if (!date) return;

      const validation = validateBirthDate(date);

      if (!validation.isValid) {
        setError(validation.error || "Invalid date selected");
        return;
      }

      setError(null);
      onDateSelect(date);
      setCurrentMonth(date);
    },
    [onDateSelect]
  );

  const goToPreviousMonth = useCallback(() => {
    setHasInteracted(true);
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    setHasInteracted(true);
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);

      const maxValidYear = getValidYears()[0];
      if (newDate.getFullYear() > maxValidYear) {
        return prev;
      }

      return newDate;
    });
  }, []);

  const changeMonth = useCallback((monthIndex: number) => {
    setHasInteracted(true);
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(monthIndex);
      return newDate;
    });
  }, []);

  const generateCalendar = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay();

    const days: (Date | null)[] = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  }, []);

  const isDateDisabled = useCallback(
    (date: Date) => {
      if (!date) return true;

      const normalizedDate = new Date(date);
      normalizedDate.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (normalizedDate >= today) return true;

      if (disablePastDates && normalizedDate < today) return true;

      const effectiveMaxDate = maxDate
        ? new Date(maxDate)
        : getMinimumValidBirthDate();
      effectiveMaxDate.setHours(0, 0, 0, 0);

      const effectiveMinDate = minDate
        ? new Date(minDate)
        : new Date("1900-01-01");
      effectiveMinDate.setHours(0, 0, 0, 0);

      if (normalizedDate > effectiveMaxDate) return true;
      if (normalizedDate < effectiveMinDate) return true;
      return false;
    },
    [disablePastDates, maxDate, minDate]
  );

  const hasEnabledDatesInCurrentMonth = useCallback(() => {
    const days = generateCalendar(currentMonth);
    return days.some((date) => date && !isDateDisabled(date));
  }, [currentMonth, generateCalendar, isDateDisabled]);

  const calendarDays = generateCalendar(currentMonth);
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  if (!isOpen) return null;

  const maxValidYear = getValidYears()[0];
  const isNextDisabled =
    currentMonth.getFullYear() === maxValidYear &&
    currentMonth.getMonth() === 11;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousMonth}
              className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <select
                value={currentMonth.getMonth()}
                onChange={(e) => changeMonth(Number(e.target.value))}
                className="
                  appearance-none bg-white border border-gray-300 text-gray-700 
                  text-sm rounded-lg px-3 py-1.5 pr-8 shadow-sm cursor-pointer
                  hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400
                  transition-all duration-150
                "
              >
                {monthNames.map((month, index) => (
                  <option key={month} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-2 top-2.5 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={currentMonth.getFullYear()}
                onChange={(e) => {
                  setHasInteracted(true);
                  const newDate = new Date(currentMonth);
                  newDate.setFullYear(Number(e.target.value));
                  setCurrentMonth(newDate);
                }}
                className="
                  appearance-none bg-white border border-gray-300 text-gray-700 
                  text-sm rounded-lg px-3 py-1.5 pr-8 shadow-sm cursor-pointer
                  hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400
                  transition-all duration-150
                "
              >
                {getValidYears().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-2 top-2.5 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={goToNextMonth}
              disabled={isNextDisabled}
              className={`p-1 rounded transition-colors ${
                isNextDisabled
                  ? "cursor-not-allowed text-gray-400"
                  : "cursor-pointer hover:bg-gray-100"
              }`}
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div
                key={day}
                className="text-center text-sm font-medium text-gray-500 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((date, index) => {
              const isSelected =
                selectedDate &&
                date &&
                date.getDate() === selectedDate.getDate() &&
                date.getMonth() === selectedDate.getMonth() &&
                date.getFullYear() === selectedDate.getFullYear();

              const isToday =
                date && date.toDateString() === new Date().toDateString();
              const disabled = date ? isDateDisabled(date) : true;
              const showTodayHighlight = isToday && !disabled;

              const showTooltip =
                !hasInteracted &&
                !hasEnabledDatesInCurrentMonth() &&
                date &&
                disabled;
              const tooltipText = showTooltip
                ? "Select month and year first using the dropdowns above"
                : date && disabled && hasEnabledDatesInCurrentMonth()
                  ? "Must be older than 18 years old to sign up."
                  : "";

              return (
                <div
                  key={date ? date.toISOString() : `empty-${index}`}
                  className="relative group"
                >
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (date && !disabled) {
                        handleDateClick(date);
                      }
                    }}
                    disabled={disabled || !date}
                    type="button"
                    className={`
                      h-8 w-full rounded-md text-sm font-medium transition-colors
                      ${
                        isSelected
                          ? "border-2 border-pink-500 cursor-pointer"
                          : showTodayHighlight
                            ? "bg-blue-100 text-blue-600 border border-blue-200 cursor-pointer"
                            : "text-gray-900 hover:bg-gray-100 cursor-pointer"
                      }
                      ${
                        disabled || !date
                          ? "text-gray-400 cursor-not-allowed hover:bg-transparent opacity-50"
                          : ""
                      }
                      ${date && date.getMonth() !== currentMonth.getMonth() ? "text-gray-400" : ""}
                    `}
                  >
                    {date ? date.getDate() : ""}
                  </button>
                  {tooltipText && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20 shadow-lg">
                      {tooltipText}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                        <div className="border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
